import React, { memo, useState } from "react";
import { Card, Image, Typography, Modal, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "./ProfileCard.css";
import useUserInformation from "@/Profile/hooks/useUserInformation";
import useAuthSession from "../../../../authPatient/hooks/useAuthSession";
import ROUTES from "@/routes/patientRoutes/routePaths.js";

const { Title, Text } = Typography;

function ProfileCard({ user: propUser, onAvatarChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const { auth, effectiveUser } = useAuthSession({ auto: true });
  const user = propUser || effectiveUser || auth?.user;
  const { avatarSrc } = useUserInformation(user);

  if (!user) {
    return (
      <div style={{ textAlign: "center", color: "gray", paddingTop: 50 }}>
        {t("profileCard.noUser")}
      </div>
    );
  }

  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName ||
        user?.lastName ||
        user?.fullName ||
        user?.name ||
        t("profileCard.defaultUserName");

  const handleEditProfile = () => {
    navigate(ROUTES?.PROFILE_EDIT_GENERAL || "/profile/edit/general");
  };

  const handleOpenAvatarPreview = () => {
    setPreviewImage(
      avatarSrc || "https://cdn-icons-png.flaticon.com/512/149/149071.png"
    );
    setPreviewVisible(true);
  };

  const handleCancelPreview = () => {
    setPreviewVisible(false);
    setPreviewImage(null);
  };

    // Fonction pour extraire les initiales
    const getInitials = () => {
      const first = user?.firstName?.[0] || "";
      const last = user?.lastName?.[0] || "";
      return (
        (first + last).toUpperCase() ||
        String(displayName)
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0])
          .join("")
          .toUpperCase() ||
        "?"
      );
    };

  return (
    <Card className="profile-card">
      <span className="profile-tag" aria-label={t("profileCard.patientIdAria")}>
        {user?.identifier}
      </span>

      <button
        className="profile-edit-btn"
        onClick={handleEditProfile}
        title={t("profileCard.editProfile") || "Edit Profile"}
        aria-label={t("profileCard.editProfile") || "Edit Profile"}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
        <span>{t("profileCard.editProfile") || "Edit Profile"}</span>
      </button>

      <div className="profile-header">
        <div
          className="avatar-wrap avatar-clickable"
          aria-label={t("profileCard.preview.imageAlt")}
          role="button"
          tabIndex={0}
          onClick={handleOpenAvatarPreview}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleOpenAvatarPreview();
            }
          }}
        >
            {avatarSrc ? (
              <Image
                width={72}
                height={72}
                src={avatarSrc}
                alt={displayName}
                preview={false}
                className="profile-avatar"
                fallback="https://cdn-icons-png.flaticon.com/512/149/149071.png"
              />
            ) : (
              <div className="profile-avatar-fallback">{getInitials()}</div>
            )}
            <span
              className="avatar-status"
              aria-label={t("profileCard.onlineStatusAria")}
            />
        </div>

        <Title level={5} className="profile-name">
          {displayName}
        </Title>

        <Text className="profile-phone">
          {user?.phone ? `+${user.phone}` : t("profileCard.noPhone")}
        </Text>

        <Text className="profile-email" title={user?.email}>
          {user?.email || t("profileCard.defaultEmail")}
        </Text>
      </div>

      <Modal
        open={previewVisible}
        onCancel={handleCancelPreview}
        centered
        width={500}
        footer={null}
        closeIcon={<CloseOutlined />}
        className="avatar-preview-modal"
      >
        <div className="avatar-preview-content">
          {previewImage && (
            <div className="preview-image-wrapper">
              <img
                src={previewImage}
                alt={t("profileCard.preview.imageAlt")}
                className="preview-image"
              />
            </div>
          )}

        </div>
      </Modal>
    </Card>
  );
}

ProfileCard.propTypes = {
  user: PropTypes.object,
  onAvatarChange: PropTypes.func,
};
export default memo(ProfileCard);
